import { useCallback, useState } from "react";
import { Stack } from "@vapor/react-material";
import Button from "@vapor/react-material/Button";
import { Typography } from "@vapor/react-extended";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faTrashAlt,
  faSpinner,
  faRotateRight,
} from "@fortawesome/pro-regular-svg-icons";
import { useDocumentList } from "../../state/useDocumentList";
import { usePostDownload } from "../../../newDocuments/hooks/api-hooks/usePostDownload";
import { useEffect } from "react";
import { useClientInformationStore } from "../../../store/useClientInformationStore";
import SnackBarAlert from "../../../common-components/SnackBarAlert";
import { useTranslation } from "@onefront/react-sdk";
import { useMoveDocumentsToBin } from "../../../../core/usecases/use-move-documents-to-bin/useMoveDocumentsToBin";
import { useDeleteMultipleDocuments } from "../../../document-detail/hooks/useDeleteMultipleDocuments";
import { ConfirmationDialog } from "../../../common-components/ConfirmationDialog";
import DeleteAlert from "../../../newDocuments/document-components/DeleteAlert";

interface SelectedDocumentActionHeaderProps {
  handleDocumentsDelete: (documentId?: string[]) => void;
  handleRestoreManyDocumentsFromBin: () => void;
  isDeleteDocumentsLoading: boolean;
  restoreLoading: boolean;
}

const SelectedDocumentActionHeader = ({
  handleDocumentsDelete,
  handleRestoreManyDocumentsFromBin,
  isDeleteDocumentsLoading,
  restoreLoading,
}: SelectedDocumentActionHeaderProps) => {
  const { workspaceId } = useClientInformationStore();
  const {
    documentFilterOption,
    documentsSelected,
    handleDocumentUpdate,
    setDocumentFilterOption,
    toggleDocumentDownload,
    toggleSelectAllDocuments,
    setHasMoreDocuments,
    setOnFilterChange,
  } = useDocumentList();

  const [openSnackBar, setOpenSnackBar] = useState<{
    download: boolean;
    moveToBin: boolean;
  }>({
    download: false,
    moveToBin: false,
  });
  const [trashedDocumentIds, setTrashedDocumentIds] = useState<string[]>([]);
  const [trashDocuments, setTrashDocuments] = useState<string[]>([]);
  const [openMoveToBinDialog, setOpenMoveToBinDialog] =
    useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [responseDocumentIdsCount, setResponseDocumentIdsCount] =
    useState<Number>(0);

  const { loading, postDownloadDocs, data, hasLoaded, fileName } =
    usePostDownload({ workspaceId });
  ///////////////Put doc to bin
  const {
    data: trashData,
    fetch: moveDocumentsToTrashBin,
    loading: trashLoading,
  } = useMoveDocumentsToBin();

  ///////////////////////Delete doc from bin
  const { data: deleteData, loading: deleteLoading } =
    useDeleteMultipleDocuments(
      trashedDocumentIds,
      workspaceId,
      confirm,
      setOpenMoveToBinDialog,
      setConfirm
    );

  const { t } = useTranslation("DOCS");
  const downloadSelectedDocuments = () => {
    //If more than 20 document selected show alert and the user recive document in email
    if (documentsSelected.length > 20) {
      setOpenSnackBar((prev) => ({
        ...prev,
        download: true,
      }));
    }
    const documentListIds = documentsSelected.map(
      (document: any) => document.documentId
    );
    postDownloadDocs(documentListIds);
    toggleSelectAllDocuments(false);
  };

  useEffect(() => {
    if (trashData?.documentStatus) {
      const idsArray = Object.keys(trashData.documentStatus);
      setTrashDocuments(idsArray);
    }
  }, [trashData]);
  useEffect(() => {
    if (deleteData?.documents) {
      const idsArray = deleteData.documents.map((doc) => doc.documentId);
      setTrashDocuments(idsArray);
    }
  }, [deleteData]);

  useEffect(() => {
    if (data?.size && hasLoaded && !loading) {
      toggleDocumentDownload();
      const blob = new Blob([data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);

      if ((window as any).aptrinsic) {
        (window as any).aptrinsic("track", "docs:DOCUMENT_DOWNLOAD");
      }
    }
  }, [data, fileName, hasLoaded, loading]);

  useEffect(() => {
    ////////Update delete state with zustant for cestino and other document
    if (trashDocuments?.length) {
      handleDocumentUpdate(trashDocuments);
    }
    if (deleteData?.documents?.length) {
      const documentIds = deleteData.documents.map((doc) => doc.documentId);
      handleDocumentUpdate(trashDocuments);
      setShowAlert(true);
    }
  }, [trashDocuments, handleDocumentUpdate, loading, deleteData]);

  useEffect(() => {
    if (documentsSelected) {
      const moveToBinDocumentIds = documentsSelected.map(
        (document: any) => document.documentId
      );
      setTrashedDocumentIds(moveToBinDocumentIds);
    }
  }, [documentsSelected]);

  useEffect(() => {
    if (!trashLoading && trashData) {
      const deletedDocumentIds = Object.values(
        trashData?.documentStatus
      ).filter((value) => value === true).length;

      setResponseDocumentIdsCount(deletedDocumentIds);
    }
  }, [trashData, trashLoading]);

  const handleMoveDocumentsToBin = useCallback(async () => {
    setOpenMoveToBinDialog(false);
    const success = await moveDocumentsToTrashBin(
      workspaceId,
      trashedDocumentIds
    );
    if (success) {
      setOpenSnackBar((prev) => ({
        ...prev,
        moveToBin: true,
      }));
      handleDocumentUpdate(trashedDocumentIds);
    }
  }, [moveDocumentsToTrashBin, workspaceId, trashData, trashedDocumentIds]);

  const handleSnackBarClose = () => {
    setOpenSnackBar((prev) => ({
      ...prev,
      download: false,
    }));
  };

  const handleMoveToBinSnackBarClose = () => {
    setOpenSnackBar((prev) => ({
      ...prev,
      moveToBin: false,
    }));
    setResponseDocumentIdsCount(0);
  };

  const handleCestinoSnackLinkClick = () => {
    setOnFilterChange(true);
    setHasMoreDocuments(true);
    setDocumentFilterOption("CESTINO");
  };

  const handleDeleteDocuments = () => {
    handleDocumentsDelete();
    setOpenDeleteDialog(false);
  };

  const isVisible = () => {
    if (loading) {
      return true;
    }
    if (documentsSelected.length > 0) {
      return true;
    } else return false;
  };

  return (
    <>
      <Stack
        alignItems="center"
        direction="row"
        gap="8px"
        justifyContent="flex-start"
        padding="0px 8px"
        sx={{
          transition: "opacity 0.7s ease",
          opacity: isVisible() ? 1 : 0,
          visibility: isVisible() ? "visible" : "hidden",
        }}
        width="100%"
      >
        <Typography
          variant="bodySmall"
          sx={{
            fontSize: "12px",
            lineHeight: "16px",
          }}
        >
          {documentsSelected?.length} {t("file.selected")}
        </Typography>

        {documentFilterOption !== "CESTINO" && (
          <Button
            disabled={trashLoading || loading}
            onClick={downloadSelectedDocuments}
            startIcon={
              <FontAwesomeIcon
                icon={trashLoading || loading ? faSpinner : faFileDownload}
                spin={trashLoading || loading}
              />
            }
            variant="text"
          >
            {t("file.download")}
          </Button>
        )}

        {documentFilterOption === "CESTINO" && (
          <Button
            disabled={restoreLoading}
            onClick={handleRestoreManyDocumentsFromBin}
            startIcon={
              <FontAwesomeIcon icon={faRotateRight} spin={restoreLoading} />
            }
            variant="text"
          >
            {t("classification.restore")}
          </Button>
        )}

        <Button
          color="error"
          disabled={
            documentFilterOption === "CESTINO"
              ? isDeleteDocumentsLoading
              : trashLoading || deleteLoading || loading
          }
          onClick={
            documentFilterOption === "CESTINO"
              ? () => setOpenDeleteDialog(true)
              : () => setOpenMoveToBinDialog(true)
          }
          startIcon={
            <FontAwesomeIcon
              icon={
                trashLoading ||
                deleteLoading ||
                isDeleteDocumentsLoading ||
                loading
                  ? faSpinner
                  : faTrashAlt
              }
              spin={
                trashLoading ||
                deleteLoading ||
                loading ||
                isDeleteDocumentsLoading
              }
              style={{ fontSize: "16px" }}
            />
          }
          variant="text"
        >
          {documentFilterOption === "CESTINO"
            ? t("confirmation.deletePermanently")
            : t("classification.delete")}
        </Button>

        {showAlert && (
          <DeleteAlert
            deletedCount={deleteData?.documents?.length || 0}
            onClose={() => setShowAlert(false)}
          />
        )}
      </Stack>

      <ConfirmationDialog
        cancelButtonColor="secondary"
        cancelButtonText={t("classification.cancel")}
        cancelButtonVariant="outlined"
        confirmButtonColor={
          documentFilterOption === "CESTINO" ? "error" : "primary"
        }
        confirmButtonText={
          documentFilterOption === "CESTINO"
            ? t("confirmation.deletePermanently")
            : t("classification.delete")
        }
        confirmButtonVariant="contained"
        content={
          documentFilterOption === "CESTINO"
            ? t("confirmation.permanentDeleteMessage")
            : t("confirmation.moveToBinMessage", {
                count: documentsSelected.length,
              })
        }
        dividerVariant={
          documentFilterOption === "CESTINO" ? "alert" : "fullWidth"
        }
        handleDialogClose={
          documentFilterOption === "CESTINO"
            ? () => setOpenDeleteDialog(false)
            : () => setOpenMoveToBinDialog(false)
        }
        handleDialogConfirm={
          documentFilterOption === "CESTINO"
            ? handleDeleteDocuments
            : handleMoveDocumentsToBin
        }
        open={
          documentFilterOption === "CESTINO"
            ? openDeleteDialog
            : openMoveToBinDialog
        }
        title={
          documentFilterOption === "CESTINO"
            ? `${t("confirmation.deletePermanently")}?`
            : t("classification.delete")
        }
      />

      <SnackBarAlert
        alertMessage={t("file.download.alertMessage")}
        linkMessage={undefined}
        onClose={handleSnackBarClose}
        onLinkClick={undefined}
        open={openSnackBar.download}
        severity="error"
      />
      <SnackBarAlert
        alertMessage={t("snackBar.documentsMoveToBin", {
          count: responseDocumentIdsCount,
        })}
        linkMessage={t("snackBar.trashBin")}
        onClose={handleMoveToBinSnackBarClose}
        onLinkClick={handleCestinoSnackLinkClick}
        open={openSnackBar.moveToBin}
        severity="success"
      />
    </>
  );
};

export default SelectedDocumentActionHeader;
